
import { z } from 'zod';
import { ADVISOR_FIRM_SLUG_PATTERN } from './advisorFirmDirectory.js';
import { LINK_GENERATION_MAX } from './intakeInvitation.js';
import { isEmptyValue } from './intakeSchema.js';
import { IntakeQueueSubmissionSchema, ULID_PATTERN } from './intakeMessage.js';
import { IntakeDraftSnapshotSchema } from './intakeSnapshot.js';
import { IntakeDraftDocumentRefSchema } from './intakeStatements.js';
import { IntakeDraftMessageRemovalSchema } from './intakeRemoval.js';

/**
 * The appcore CustomType code for a saved partial intake. One constant, because the string
 * appears in three independent places that must agree: this package's pack type def
 * (`STEWARD_ADVISOR_FIRM_TYPE_DEFS`), the `/system/*` paths the background roles are
 * granted in CDK, and appcore's per-actor `typeCodes` scope. A typo in any of them is a
 * 403 at appcore that reads like an IAM problem.
 */
export const INTAKE_DRAFT_TYPE_CODE = 'intake-draft' as const;

/** The wire version of the draft queue message. See `intakeMessage.ts` §Versioning. */
export const INTAKE_DRAFT_MESSAGE_VERSION = 1;

/**
 * The lifecycle of a stored draft row. `draft` is live; `submitted` is the tombstone left
 * behind once the prospect sent it (sections emptied, `submittedIntakeId` set);
 * `tombstoned` is the same shape reached any other way.
 *
 * The reader and the writer both refuse to act on anything but `draft`, which is what makes
 * "revoked on submission" a STATE fact needing no revocation row.
 */
export const INTAKE_DRAFT_STATUSES = ['draft', 'submitted', 'tombstoned'] as const;
export const IntakeDraftStatusSchema = z.enum(INTAKE_DRAFT_STATUSES);
export type IntakeDraftStatus = (typeof INTAKE_DRAFT_STATUSES)[number];


/** Stands in for the firm's name when there is none to use. Reads as one clean sentence
 *  either way, which is the whole requirement on it. */
export const INTAKE_SHARING_CONSENT_FALLBACK_FIRM = 'this firm';

/**
 * Upper bound on a stored consent sentence, in UTF-16 units (`String.length`, which is how the
 * lead Lambda, the writer and {@link readIntakeSharingConsent} all count it).
 *
 * The template is 170 units including its 6-unit `{firm}` token, so a sentence is 164 units
 * plus the firm name. A firm name of 120 astral characters (the directory's cap counts code
 * points, this cap counts units) is 240 units, giving 404 against 500 (measured 2026-10-07,
 * pinned in `intakeDraft.test.ts`). The name is copied into the sentence literally (see
 * `intakeSharingConsentSentence`), so no `$` sequence changes the length.
 */
export const INTAKE_SHARING_CONSENT_MAX_LENGTH = 500;

/**
 * The consent sentence a person agrees to, and the exact text stored as the record of it
 * (wording set 2026-10-07, crossmarc/steward-app#2158).
 *
 * This line, and only this line, holds the wording. Every caller composes the sentence through
 * {@link intakeSharingConsentSentence}, and tests compare against that function's output, not
 * a literal of their own.
 *
 * Rules a rewrite must keep, each pinned in `intakeDraft.test.ts`:
 *   - `{firm}` ({@link INTAKE_SHARING_CONSENT_FIRM_TOKEN}) appears exactly once.
 *   - The sentence names no button label.
 *
 * Editing it changes future records only. A stored `sharingConsentText` is written once and
 * never re-derived, so rows already written keep the sentence their owner read.
 */
export const INTAKE_SHARING_CONSENT_TEMPLATE =
  'Everything you add here is shared with {firm} as you add it, starting with your name and contact '
  + 'details, and including any files you attach and any accounts you connect.';

/** The substitution token in {@link INTAKE_SHARING_CONSENT_TEMPLATE}. Named so the test that
 *  requires it and the function that replaces it cannot drift. */
export const INTAKE_SHARING_CONSENT_FIRM_TOKEN = '{firm}';

/**
 * The consent sentence for one firm: {@link INTAKE_SHARING_CONSENT_TEMPLATE} with the firm's
 * display name in it.
 *
 * The ONE composer. The browser renders the person's copy from it and the server composes the
 * stored copy from it. The client sends a boolean and never the text.
 *
 * ⚠️ Every `.replace` here is `replaceAll`-free on purpose: the token appears once and a
 * template that repeated it would read as two different firms to a prospect.
 *
 * ⚠️ The trimmed name goes in through a replacer FUNCTION, so it is copied literally. A replacement
 * STRING would read `$$`, `$&`, `` $` `` and `$'` inside a firm name as patterns, showing and
 * recording a different name from the one the firm chose (crossmarc/steward-app#2081).
 */
export function intakeSharingConsentSentence(firmName?: string | null): string {
  const named = typeof firmName === 'string' ? firmName.trim() : '';
  const who = named === '' ? INTAKE_SHARING_CONSENT_FALLBACK_FIRM : named;
  return INTAKE_SHARING_CONSENT_TEMPLATE.replace(INTAKE_SHARING_CONSENT_FIRM_TOKEN, () => who);
}

/**
 * An ISO-8601 instant as this channel writes one: `new Date().toISOString()`, or the same with
 * an explicit offset. Deliberately NOT a general date parser — see
 * {@link readIntakeSharingConsent} for why `Date.parse` alone is not a shape check.
 */
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

/** A recorded consent, as every consumer reads it: both halves or nothing. */
export interface IntakeSharingConsent {
  /** ISO. The façade's clock when it accepted the consenting claim. */
  agreedAt: string;
  /** The exact sentence that was shown. See the section header. */
  text: string;
}

export function readIntakeSharingConsent(
  attributes: Record<string, unknown> | undefined,
): IntakeSharingConsent | null {
  if (!attributes) return null;
  if (!Object.hasOwn(attributes, 'sharingConsentAt')) return null;
  if (!Object.hasOwn(attributes, 'sharingConsentText')) return null;
  const agreedAt = attributes['sharingConsentAt'];
  const text = attributes['sharingConsentText'];
  if (typeof agreedAt !== 'string' || !ISO_INSTANT.test(agreedAt)) return null;
  if (Number.isNaN(Date.parse(agreedAt))) return null;
  if (typeof text !== 'string' || text.trim() === '') return null;
  if (text.length > INTAKE_SHARING_CONSENT_MAX_LENGTH) return null;
  return { agreedAt, text };
}

/**
 * Whether the FIRM can see this `intake-draft` row, for the one purpose of deciding whether it
 * may expire (crossmarc/steward-app#2549; Matt's ruling 2026-10-24, docs/29 D5 and docs/40 F5).
 *
 * "Can see" requires an owner (a non-empty `claimEmailHash`: an unclaimed row is listed by no
 * firm screen) and then one of two things: the row carries a complete sharing consent (read by
 * {@link readIntakeSharingConsent}, so a half pair or a malformed one counts as none), or it
 * carries an attached `invitationId` (a ULID). A draft the firm can see does not expire: the firm
 * closes or deletes it. ⚠️ The invitation half reads only the STORED id, not whether that
 * invitation is still live: after the firm cancels it, a no-consent draft stays unexpiring until
 * its next save drops the id (crossmarc/steward-app#2560). A draft it cannot see keeps docs/29 D5's sixty days, because nothing
 * else can ever reach it to remove it.
 *
 * It is read through {@link intakeDraftExpires}, which adds the one exception (a SUBMITTED
 * tombstone), so the writer that stamps `expiresAt` and the sweeper that deletes on it cannot
 * disagree. Pinned both ways in `intakeDraft.test.ts`. The callers' suites pin consent and
 * invitation, but every caller fixture carries an owner, so the owner clause is pinned only here.
 *
 * ⚠️ It says nothing about whether the invitation is still live, and nothing about what the firm
 * may READ (that is `readIntakeSharingConsent` alone, in the façade's draft summary). A
 * cancelled invitation is dropped from the row by the writer's next save, and from then on the
 * row counts as not seen unless it also carries a consent.
 */
export function intakeDraftFirmCanSee(attributes: Record<string, unknown> | undefined): boolean {
  if (!attributes) return false;
  // A row with no owner is listed by no firm screen (the façade counts unclaimed drafts, never
  // shows them), so whatever else it carries, the firm cannot see it (review F3 of #2549).
  const owner = attributes['claimEmailHash'];
  if (typeof owner !== 'string' || owner === '') return false;
  if (readIntakeSharingConsent(attributes) !== null) return true;
  const invitationId = attributes['invitationId'];
  return typeof invitationId === 'string' && ULID_PATTERN.test(invitationId);
}

/**
 * Whether this `intake-draft` row is on the sixty-day expiry (docs/29 D5), i.e. whether the
 * writer stamps `expiresAt` on it and the sweeper may plan it for deletion
 * (crossmarc/steward-app#2549). The ONE predicate both use: `services/intake-writer/src/draft.ts`
 * (`withDraftExpiry`) applies it to the attributes it is about to write, and
 * `services/intake-retention/src/index.ts` applies it to every listed row before planning, so a
 * firm-visible row still carrying a stamp from before #2549 is never planned.
 *
 * True when the firm cannot see the draft ({@link intakeDraftFirmCanSee}), and ALSO for a
 * `status: 'submitted'` row whatever it carries. 🚨 That exception is a judgement call made in
 * the #2549 branch, not part of Matt's ruling as worded: a submitted draft is a tombstone that
 * still holds the prospect's full `sections`, the firm works from the `advisor-intake` it
 * produced (which has its own retention window), and every submitted draft carries a consent
 * (the submit requires one). Without the exception every tombstone would be kept forever,
 * which would outlive the submitted-intake retention the ruling leaves unchanged. The dev
 * census of 2026-10-24 (this branch's author, a drained scan of `appcore-dev`) found 149 of 155
 * intake-draft rows were exactly that shape.
 *
 * Any status other than `'submitted'` (including an absent or unknown one) is treated as a
 * draft: an unrecognised status never puts a firm-visible row on the expiry. (A row the firm
 * cannot see is on the expiry whatever its status.)
 */
export function intakeDraftExpires(attributes: Record<string, unknown> | undefined): boolean {
  if (attributes?.['status'] === 'submitted') return true;
  return !intakeDraftFirmCanSee(attributes);
}

/**
 * A partial submission: every section optional, everything else identical.
 *
 * Derived from {@link IntakeQueueSubmissionSchema} rather than from `IntakeSubmissionShape`
 * directly, so the honeypot is dropped by the SAME `.omit()` the queue message already
 * uses. One place says "the honeypot is an anti-spam control, not intake data"; adding a
 * second would be the drift this file's header argues against.
 *
 * Read the header before widening this. `.partial()` is top-level only, on purpose.
 */
export const IntakeDraftSectionsSchema = IntakeQueueSubmissionSchema.partial();

export type IntakeDraftSections = z.infer<typeof IntakeDraftSectionsSchema>;

/**
 * Carried by every kind (2026-10-07). There is NO draft id on a write message, with one exception
 * (`link-settle`, which names a draft only to key a lookup): the writer finds the draft from the
 * owner binding stored under `(ownerUserId, firm)` and nowhere else.
 */
const draftCommonFields = {
  v: z.literal(INTAKE_DRAFT_MESSAGE_VERSION),
  /**
   * The firm's slug, from the request PATH (never a body), lower-cased, and resolved against the
   * firm directory by the façade before the message is queued. The writer resolves it again and
   * trusts nothing about the first resolution: a slug it cannot resolve is acknowledged and
   * dropped as `unknown_firm`.
   */
  firmId: z.string().regex(ADVISOR_FIRM_SLUG_PATTERN),
  /**
   * The façade's clock when it accepted this write (ISO 8601). The writer refuses a `save`
   * whose `savedAt` is older than the stored row's. `.max(64)` because the pattern's
   * fractional-second group is otherwise unbounded.
   */
  savedAt: z.iso.datetime().max(64),
};

/**
 * Who is writing. The façade sets both from appcore's answer to the caller's own token
 * (`GET /me/intake-authority`) and from nothing in the request. `ownerEmail` is the account's
 * stored address. It is recorded as the signed-in address and is not the ownership key (the
 * draft is owned through `ownerUserId`). The draft writer also compares it with an
 * invitation's address when it decides whether that invitation attaches
 * (crossmarc/steward-app#2176).
 */
const draftWriteFields = {
  ownerUserId: z.string().min(1).max(200).regex(/^[^\s#]+$/),
  ownerEmail: z.email().max(254),
};

/** The opaque code an invitation mail carries: 43 url-safe characters. */
export const INTAKE_INVITATION_CODE_PATTERN = /^[A-Za-z0-9_-]{43}$/;

/**
 * Every key the signed-in draft routes accept in a JSON body, per route. Each route's schema
 * is strict, so a body carrying any other key is refused with a 400.
 * `services/steward-app-api/src/__tests__/intakeRequestKeys.test.ts` checks that each route's
 * schema declares exactly these keys. There is no `statements` key (#2310): the façade's strict
 * schema refuses it with a 400, like any other unknown key.
 */
export const INTAKE_DRAFT_SAVE_REQUEST_KEYS = [
  'sections', 'snapshot', 'sharingConsent', 'sectionIndex', 'invitationCode',
] as const;
export type IntakeDraftSaveRequestKey = (typeof INTAKE_DRAFT_SAVE_REQUEST_KEYS)[number];

/**
 * The MINT request of the client-document upload (crossmarc/steward-app#2307): what a person is
 * about to add and where it goes. `rowId` and `accountId` are alternatives, never both.
 * No route schema declares this list yet, so no test compares one to it.
 *
 * Neither list below has a `clientId`, `userId`, `documentId` or `source` key, and the
 * `intakeDraft.test.ts` census pins that: the client slot and the user come from the writer's
 * own records, and the document id from the upload ledger's key.
 */
export const INTAKE_DRAFT_UPLOAD_MINT_REQUEST_KEYS = [
  'name', 'contentType', 'size', 'section', 'rowId', 'accountId',
] as const;
export type IntakeDraftUploadMintRequestKey = (typeof INTAKE_DRAFT_UPLOAD_MINT_REQUEST_KEYS)[number];

/** The COMMIT request of the same upload: the id the mint answered with and the name and type the
 *  mint was given (appcore needs them again to find the staged object). 🚨 **No placement key:**
 *  where the file goes was decided and validated at the mint and is stored on the upload ledger
 *  row, so nothing sent at the commit can move it (crossmarc/steward-app#2307, fix round 1,
 *  2026-10-12). `uploadId` names a ledger item; it is never written to the draft from here. */
export const INTAKE_DRAFT_UPLOAD_COMMIT_REQUEST_KEYS = [
  'uploadId', 'name', 'contentType',
] as const;
export type IntakeDraftUploadCommitRequestKey = (typeof INTAKE_DRAFT_UPLOAD_COMMIT_REQUEST_KEYS)[number];

/** `POST /intake/{firmSlug}/draft/link`. */
export const INTAKE_DRAFT_LINK_REQUEST_KEYS = ['publicToken'] as const;
export type IntakeDraftLinkRequestKey = (typeof INTAKE_DRAFT_LINK_REQUEST_KEYS)[number];

/**
 * `POST /intake/{firmSlug}/link` (kept link S3, 2026-10-07): the route that REPLACED
 * `/draft/link`. The permit id comes from `POST /intake/{firmSlug}/link-permit`. The old
 * constant above still describes what the SPA sends today (the old, now unmounted route); the
 * SPA stage switches to this one and deletes the old.
 */
export const INTAKE_LINK_REQUEST_KEYS = ['publicToken', 'permitId'] as const;
export type IntakeLinkRequestKey = (typeof INTAKE_LINK_REQUEST_KEYS)[number];

/**
 * `save` — a section was completed. Carries the whole partial draft, not a delta.
 *
 * 🚨 **No `documents` key, and the schema is strict, so a body that sends one is refused rather
 * than stripped** (#2307). **No `statements` key either since #2308 PR 2 (2026-10-12):** the
 * bank's statements are client documents too, so every file on a draft, the bank's and the
 * person's, reaches `documents` through `document-attach` alone and no save carries a list of
 * them. Since #2310 the façade's request schema no longer accepts the key either, so
 * it never reaches this message; `intakeStatements.test.ts` pins that the message refuses it.
 */
export const IntakeDraftSaveMessageSchema = z.strictObject({
  ...draftCommonFields,
  ...draftWriteFields,
  kind: z.literal('save'),
  sections: IntakeDraftSectionsSchema,
  /**
   * The account-link snapshot this draft holds (crossmarc/steward-app#1014).
   *
   * 🚨 **A SIBLING of `sections`** (as `statements` was, until #2308 PR 2) — `sections` is
   * `IntakeQueueSubmissionSchema.partial()` and nothing else, and a `snapshot` key inside it
   * would change the live v1/v2 SUBMISSION wire contract, the PDF export's traversal and the
   * advisor's filed copy, in the PR that adds a Connect button.
   *
   * 🚨 **Ignored by the writer since docs/43 slice 4d.** The snapshot is stored on the client's `connected` row and
   * merged there by the `link-connected` message; a `save` carries
   * answers only. The field stays optional on the wire so an older bundle's autosave still parses, and the writer
   * drops it rather than writing it: a stale tab can no longer replace the connected row's snapshot.
   */
  snapshot: IntakeDraftSnapshotSchema.optional(),
  sharingConsent: z.boolean().optional(),
  sectionIndex: z.number().int().min(0).max(999).optional(),
  invitationCode: z.string().regex(INTAKE_INVITATION_CODE_PATTERN).optional(),
});

/**
 * `submit` — turn the stored draft into an `advisor-intake` and tombstone it.
 *
 * Deliberately carries NO `sections`. Submit revalidates the full `IntakeSubmissionSchema`
 * server-side against the row the writer reads, never against the request: a message that
 * could carry the payload would be a way to submit content the draft never held.
 */
export const IntakeDraftSubmitMessageSchema = z.strictObject({
  ...draftCommonFields,
  ...draftWriteFields,
  kind: z.literal('submit'),
});

export const IntakeDraftRemoveMessageSchema = z.strictObject({
  ...draftCommonFields,
  ...draftWriteFields,
  kind: z.literal('remove'),
  removal: IntakeDraftMessageRemovalSchema,
});

/** The ledger key of one client-document upload: the `entityId` appcore's mint issued. */
const uploadIdField = z.string().regex(ULID_PATTERN);

const documentRefShape = IntakeDraftDocumentRefSchema.shape;

/**
 * `document-attach` (#2307): a client-document upload committed in appcore, and now to be
 * recorded on the draft. Built by the façade from the reader's answer to the commit.
 *
 * 🚨 **No `documentId` and no `source` key.** The strict schema refuses both. The stored
 * reference's `documentId` is the upload ledger's key (`uploadId` here names the ledger item and
 * the writer checks the item before it writes anything), and `source` is read by the writer from
 * the ledger row's `origin` (`client` or `plaid`), never from this message. A message that could carry either would make the id a browser could choose reach the
 * draft by way of the façade's copy.
 *
 * The remaining fields are the reference's description, with the same bounds (`name` at most 120
 * characters, the three document content types). 🚨 **No `section`, `accountId` or `rowId`:** the
 * placement is read by the writer from the upload ledger row, where the mint stored what it
 * validated, so no field of a message (and no field of the commit request the façade built it
 * from) can place a file anywhere else.
 */
export const IntakeDraftDocumentAttachMessageSchema = z.strictObject({
  ...draftCommonFields,
  ...draftWriteFields,
  kind: z.literal('document-attach'),
  uploadId: uploadIdField,
  name: documentRefShape.name,
  contentType: documentRefShape.contentType,
  size: documentRefShape.size,
  uploadedAt: documentRefShape.uploadedAt,
});

/**
 * `document-settle` (#2307): enqueued with a delay, before the commit is sent, to resolve a
 * commit whose outcome was never learned. It names the ledger item and nothing else: the writer
 * decides what to do from the item's state, and the message cannot say.
 */
export const IntakeDraftDocumentSettleMessageSchema = z.strictObject({
  ...draftCommonFields,
  ...draftWriteFields,
  kind: z.literal('document-settle'),
  uploadId: uploadIdField,
});

/**
 * `link-settle` (#2308): the delayed reconcile for every upload one account link minted. Where
 * `document-settle` names one ledger item, this names a LINK, and the writer finds that link's
 * upload ids on its index row (`addLinkUpload` in `@steward-app/intake-claims`).
 *
 * 🚨 **This is the one draft-family message that names a draft** (`draftId`), because the draft
 * that minted a link's uploads may no longer be the one the owner binding resolves to today. The
 * writer uses it only as part of the key of the index row and of each ledger row, and acts on a row
 * only when that row's recorded owner is the message's `ownerUserId`. It is not a way to choose a
 * draft to WRITE: the settle writes no reference. Nothing else is accepted: no upload id, no
 * client id, no action, no source.
 */
export const IntakeDraftLinkSettleMessageSchema = z.strictObject({
  ...draftCommonFields,
  ...draftWriteFields,
  kind: z.literal('link-settle'),
  linkId: z.string().regex(ULID_PATTERN),
  draftId: z.string().regex(ULID_PATTERN),
});

/** The six kinds, discriminated on `kind`. */
export const IntakeDraftMessageSchema = z.discriminatedUnion('kind', [
  IntakeDraftSaveMessageSchema,
  IntakeDraftSubmitMessageSchema,
  IntakeDraftRemoveMessageSchema,
  IntakeDraftDocumentAttachMessageSchema,
  IntakeDraftDocumentSettleMessageSchema,
  IntakeDraftLinkSettleMessageSchema,
]);

export type IntakeDraftSaveMessage = z.infer<typeof IntakeDraftSaveMessageSchema>;
export type IntakeDraftSubmitMessage = z.infer<typeof IntakeDraftSubmitMessageSchema>;
export type IntakeDraftRemoveMessage = z.infer<typeof IntakeDraftRemoveMessageSchema>;
export type IntakeDraftDocumentAttachMessage = z.infer<typeof IntakeDraftDocumentAttachMessageSchema>;
export type IntakeDraftDocumentSettleMessage = z.infer<typeof IntakeDraftDocumentSettleMessageSchema>;
export type IntakeDraftLinkSettleMessage = z.infer<typeof IntakeDraftLinkSettleMessageSchema>;
export type IntakeDraftMessage = z.infer<typeof IntakeDraftMessageSchema>;

/** The kinds, as a value, for exhaustiveness checks and tests. */
export const INTAKE_DRAFT_MESSAGE_KINDS = [
  'save', 'submit', 'remove', 'document-attach', 'document-settle', 'link-settle',
] as const;
export type IntakeDraftMessageKind = (typeof INTAKE_DRAFT_MESSAGE_KINDS)[number];

/**
 * Parses one queue message, returning `undefined` for anything this deploy does not
 * understand — an unknown `kind`, a missing or wrong `v`, a malformed id, an undeclared
 * key, or a `sections` object that fails the draft schema.
 *
 * `undefined` rather than a throw, and rather than a partially-parsed object, because the
 * consumer's decision on an unparseable message is a policy question it has to make
 * explicitly (steward-app#1012 PR 5 acks and counts a forged one rather than
 * dead-lettering it, since an attacker with a curl loop would otherwise page the on-call
 * through an alarm whose threshold is 1). A parser that threw would push that decision
 * into a catch block, where "which failure was this" is a string match.
 *
 * A queue holds messages from the previous deploy for minutes after a rollout, and a DLQ
 * holds them for fourteen days, so "I do not understand this one" has to be a precise
 * answer rather than a plausible-looking wrong record.
 */
export function parseIntakeDraftMessage(value: unknown): IntakeDraftMessage | undefined {
  const parsed = IntakeDraftMessageSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

export const IntakeInvitationSupersededMessageSchema = z.strictObject({
  v: z.literal(INTAKE_DRAFT_MESSAGE_VERSION),
  kind: z.literal('invitation-superseded'),
  invitationId: z.string().regex(ULID_PATTERN),
  /** The invitation's generation AFTER the cancel: links minted below it stop working. */
  linkGeneration: z.number().int().min(1).max(LINK_GENERATION_MAX),
});
export type IntakeInvitationSupersededMessage = z.infer<typeof IntakeInvitationSupersededMessageSchema>;

/** {@link parseIntakeDraftMessage}'s contract, for the `invitation-superseded` message. */
export function parseIntakeInvitationSupersededMessage(
  value: unknown,
): IntakeInvitationSupersededMessage | undefined {
  const parsed = IntakeInvitationSupersededMessageSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

/**
 * Whether a piece of a draft holds anything a person actually typed, recursing through
 * whatever shape a section or sub-section is (crossmarc/steward-app, "Prospect Stages,
 * derived": a saved-but-untouched intake always captioned every section as started).
 *
 * Structural rather than schema-driven, and deliberately so — a value here can be a whole
 * `sections` bag, a single section, or a leaf, and this function has to be right for all
 * three without knowing which one it was handed. `toSubmission`
 * (`src/intake/draft.ts`, the SPA) sets every one of the top-level keys on the
 * FIRST save, most of them to an empty shell (`{}`, `[]`, `''`, a boolean default), so
 * "the key is present" answers a different question from "someone answered it" — and it
 * is the second one both surfaces below need:
 *
 *   - the SPA already uses this (as `sectionsHaveAnswers`, `src/intake/draftSync.ts`) to
 *     decide whether a save round-tripped a real change;
 *   - the façade (`services/steward-app-api/src/handlers/advisor.ts`'s `toDraftSummary`)
 *     counts, per section, whether THIS predicate is true of that section's value — that
 *     is the number in "N of M sections" and the derived stage (`src/advisor/
 *     prospectStages.ts`) are built from.
 *
 * Two places computing "started" on the same underlying number would be the drift this
 * function exists to prevent, so it lives here, in the package both sides already import
 * from, rather than being copied — the façade cannot import `src/` (separate deploy
 * artifacts, steward-app#319), and the SPA should not re-derive server-trusted logic.
 *
 * ⚠️ `false` counts as no answer, on purpose, even though `hasSpousePartner: false` is a
 * real, deliberate choice a person made. There is no way to tell that apart from the
 * default `emptyDraft()` ships with from the payload alone, and the false positive this
 * avoids (a section that looks started because of an unset default) is worse for this
 * predicate's purpose than the false negative it accepts (a spousePartner section whose
 * only answer was "no").
 *
 * Defined as the exact negation of `isEmptyValue` (`intakeSchema.ts`, exported
 * 2026-09-19 for this) rather than as a second copy of the same recursion — code
 * review on this branch found the two already computed the same thing in mirror
 * image. The negation is exact, branch-for-branch, for every value this function
 * is ever actually handed: `null`, `undefined`, a `string`, a `boolean`, a `number`
 * (including `NaN` — `isEmptyValue` has no `number` branch and falls through to
 * `false`, i.e. "not empty", which negates to `true`, matching the explicit
 * `number` branch this function used to carry), an `Array`, and a plain object,
 * recursively. That covers every shape JSON can produce, which is the only kind
 * of value this ever sees: `value` here is always something that came out of
 * DynamoDB as parsed JSON (a draft row's `sections` attribute, or a piece of it),
 * never a live JS value the SPA holds in memory before serializing.
 *
 * It is NOT exact for `function`, `symbol` or `bigint` — `isEmptyValue`'s
 * catch-all is `return false` (not empty), which negates to `true` (an answer),
 * where this function's old standalone body fell through its final `return false`
 * (no answer) for those same three types. That divergence is real, not just
 * theoretical, but it is unreachable here for the JSON-only reason above, not
 * because the two functions actually agree on it — table-driven test below pins
 * the JSON-representable cases this function is contracted to get right, not the
 * three it never sees.
 *
 * The two functions are kept as separate call sites on purpose, not merged into
 * one shared entry point, because they run at different moments — `isEmptyValue`
 * gates a fully shaped, already-`IntakeSubmissionSchema`-typed object at
 * `.refine()` time; this one counts per-section answers on an unvalidated
 * `sections` blob read straight off a draft row.
 */
export function sectionHasAnswer(value: unknown): boolean {
  return !isEmptyValue(value);
}

export const IntakeInvitationMailMessageSchema = z.strictObject({
  v: z.literal(INTAKE_DRAFT_MESSAGE_VERSION),
  kind: z.literal('invitation-mail'),
  firmId: z.string().regex(ADVISOR_FIRM_SLUG_PATTERN),
  invitationId: z.string().regex(ULID_PATTERN),
  linkGeneration: z.number().int().min(1).max(LINK_GENERATION_MAX),
  email: z.email().max(254),
  ttlSeconds: z.number().int().min(1).max(7 * 24 * 60 * 60),
  savedAt: z.iso.datetime().max(64),
});
export type IntakeInvitationMailMessage = z.infer<typeof IntakeInvitationMailMessageSchema>;

export function parseIntakeInvitationMailMessage(value: unknown): IntakeInvitationMailMessage | undefined {
  const parsed = IntakeInvitationMailMessageSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

/**
 * `snapshot-received-mail` (crossmarc/steward-app#2438): the intake writer asks the snapshot
 * mailer to send the prospect one plain confirmation after their FIRST successful submit.
 *
 * Sent by `services/intake-writer` (never the façade, which cannot know the submit was
 * accepted), consumed by `intakeClaimConsumer.ts`, which holds no appcore access and so cannot
 * look anything up: the message carries what the mail needs and nothing else.
 *
 *   - `firmId`: the slug the mailer resolves the firm's name, noun and link from, exactly as
 *     the invitation mail does.
 *   - `draftId`: the mailer's once-only key. Not shown to anyone.
 *   - `email`: the address the writer holds for the draft, `invitedEmail ?? claimEmail`
 *     (the invitation's own address when the draft arrived from one, else the proven sign-in
 *     address). Never an address typed into the form.
 *
 * Nothing from the snapshot itself rides on it: no name, no answer, no account.
 */
export const IntakeSnapshotReceivedMailMessageSchema = z.strictObject({
  v: z.literal(INTAKE_DRAFT_MESSAGE_VERSION),
  kind: z.literal('snapshot-received-mail'),
  firmId: z.string().regex(ADVISOR_FIRM_SLUG_PATTERN),
  draftId: z.string().regex(ULID_PATTERN),
  email: z.email().max(254),
});
export type IntakeSnapshotReceivedMailMessage = z.infer<typeof IntakeSnapshotReceivedMailMessageSchema>;

export function parseIntakeSnapshotReceivedMailMessage(value: unknown): IntakeSnapshotReceivedMailMessage | undefined {
  const parsed = IntakeSnapshotReceivedMailMessageSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}
